import { useQuery } from 'react-query';
import CardRelatorio from '../CardRelatorio';
import axios from 'axios';
import Skeleton from '../../../components/layout/Skeleton';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useEffect, useState } from 'react';
import HBox from '../../../components/layout/HBox';
import Botao from '../../../components/Botao';
import { differenceInDays } from 'date-fns';
import { translateFilterFinanceiro, currencyFormat } from './util';

type Props = {
  dataInicio: string;
  dataFim: string;
};

const parseDate = (v: string) =>
  new Date(v.substring(0, 10) + 'T00:00:00.000').toLocaleDateString();

const CardSerieFinanceira = ({ dataInicio, dataFim }: Props) => {
  const calcBestInterval = () => {
    const days = differenceInDays(dataFim, dataInicio);
    if (days <= 7) return 'dia';
    if (days <= 62) return 'semana';
    return 'mes';
  };

  const [selectedPeriod, setSelectedPeriod] = useState<'dia' | 'semana' | 'mes'>(
    calcBestInterval,
  );

  useEffect(() => {
    setSelectedPeriod(calcBestInterval());
  }, [dataInicio, dataFim]);

  const dimensao = `data_vencimento_${selectedPeriod}`;

  const q = useQuery(
    ['financeiroSerie', selectedPeriod, dataInicio, dataFim],
    async () => {
      const qs = new URLSearchParams();
      qs.append('facts', 'total_entradas,total_saidas');
      qs.append('dimensions', dimensao);
      qs.append('filter', translateFilterFinanceiro(dataInicio, dataFim));

      const res = await axios.get(
        '/api/relatorios/lancamento_financeiro?' + qs.toString(),
      );
      // pg retorna sum() de numeric como string; o Recharts precisa de number.
      return (res.data.result as any[]).map(r => ({
        ...r,
        total_entradas: +r.total_entradas,
        total_saidas: +r.total_saidas,
      }));
    },
    { refetchOnWindowFocus: false },
  );

  return (
    <CardRelatorio titulo="Entradas x Saídas" style={{ height: '420px' }}>
      <HBox>
        <Botao
          variant={selectedPeriod === 'dia' ? 'outline-primary' : 'outline-secondary'}
          onClick={() => setSelectedPeriod('dia')}
        >
          Dia
        </Botao>
        <Botao
          variant={selectedPeriod === 'semana' ? 'outline-primary' : 'outline-secondary'}
          onClick={() => setSelectedPeriod('semana')}
        >
          Semana
        </Botao>
        <Botao
          variant={selectedPeriod === 'mes' ? 'outline-primary' : 'outline-secondary'}
          onClick={() => setSelectedPeriod('mes')}
        >
          Mês
        </Botao>
      </HBox>
      {q.isFetching ? (
        <Skeleton format="box" />
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={q.data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={dimensao} tickFormatter={v => parseDate(v)} />
            <YAxis tickFormatter={v => currencyFormat.format(v)} width={90} />
            <Tooltip
              labelFormatter={v => parseDate(v as string)}
              formatter={(v: any) => currencyFormat.format(+v)}
            />
            <Legend />
            <Bar dataKey="total_entradas" name="Entradas" fill="#22c55e" />
            <Bar dataKey="total_saidas" name="Saídas" fill="#7339ac" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </CardRelatorio>
  );
};

export default CardSerieFinanceira;
